import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../contexts/TranslationContext.tsx';
import { API_BASE } from '../../utils/apiEndpoints';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape.ts';

interface TheorySublessonAdmin {
  id: number;
  slug: string;
  title: string;
  title_i18n: Record<string, string> | null;
  sort_order: number;
}

interface TheoryLessonAdmin {
  id: number;
  slug: string;
  title: string;
  title_i18n: Record<string, string> | null;
  sort_order: number;
  sublessons: TheorySublessonAdmin[];
}

interface TheoryCategoryAdmin {
  id: number;
  slug: string;
  title: string;
  title_i18n: Record<string, string> | null;
  icon: string;
  sort_order: number;
  lessons: TheoryLessonAdmin[];
}

type EditTarget =
  | { kind: 'lesson'; mode: 'create'; categoryId: number }
  | { kind: 'lesson'; mode: 'edit'; lesson: TheoryLessonAdmin; categoryId: number }
  | { kind: 'sublesson'; mode: 'create'; lessonId: number }
  | { kind: 'sublesson'; mode: 'edit'; sublesson: TheorySublessonAdmin; lessonId: number };

interface FormState {
  slug: string;
  titleEn: string;
  titleRu: string;
  sortOrder: string;
}

function emptyForm(): FormState {
  return { slug: '', titleEn: '', titleRu: '', sortOrder: '0' };
}

function formFromLesson(lesson: TheoryLessonAdmin): FormState {
  return {
    slug: lesson.slug,
    titleEn: lesson.title_i18n?.en ?? lesson.title,
    titleRu: lesson.title_i18n?.ru ?? '',
    sortOrder: String(lesson.sort_order),
  };
}

function formFromSublesson(sublesson: TheorySublessonAdmin): FormState {
  return {
    slug: sublesson.slug,
    titleEn: sublesson.title_i18n?.en ?? sublesson.title,
    titleRu: sublesson.title_i18n?.ru ?? '',
    sortOrder: String(sublesson.sort_order),
  };
}

function displayTitle(
  item: { title: string; title_i18n: Record<string, string> | null },
  lang: 'ru' | 'en'
) {
  return item.title_i18n?.[lang] ?? item.title;
}

export function TheoryTopicsAdminSection() {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const lang = currentLanguage === 'ru' ? 'ru' : 'en';

  const [categories, setCategories] = useState<TheoryCategoryAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(() => new Set());
  const [expandedLessons, setExpandedLessons] = useState<Set<number>>(() => new Set());
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingLessonId, setDeletingLessonId] = useState<number | null>(null);
  const [deletingSublessonId, setDeletingSublessonId] = useState<number | null>(null);

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/theory/admin/library`, { credentials: 'include' });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }
      if (!r.ok) throw new Error(t('admin.theoryTopicsLoadFailed'));
      const data = await r.json();
      const items = Array.isArray(data?.categories) ? data.categories : [];
      setCategories(items);
      setExpandedCategories((prev) => {
        if (prev.size > 0) return prev;
        return new Set(items.map((c: TheoryCategoryAdmin) => c.id));
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.unknownError'));
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  useCloseOnEscape(editTarget != null, () => {
    setEditTarget(null);
    setFormError(null);
  });

  const openCreateLesson = (categoryId: number) => {
    setEditTarget({ kind: 'lesson', mode: 'create', categoryId });
    setForm(emptyForm());
    setFormError(null);
  };

  const openEditLesson = (lesson: TheoryLessonAdmin, categoryId: number) => {
    setEditTarget({ kind: 'lesson', mode: 'edit', lesson, categoryId });
    setForm(formFromLesson(lesson));
    setFormError(null);
  };

  const openCreateSublesson = (lessonId: number) => {
    setEditTarget({ kind: 'sublesson', mode: 'create', lessonId });
    setForm(emptyForm());
    setFormError(null);
    setExpandedLessons((prev) => new Set(prev).add(lessonId));
  };

  const openEditSublesson = (sublesson: TheorySublessonAdmin, lessonId: number) => {
    setEditTarget({ kind: 'sublesson', mode: 'edit', sublesson, lessonId });
    setForm(formFromSublesson(sublesson));
    setFormError(null);
  };

  const toggleCategory = (id: number) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleLesson = (id: number) => {
    setExpandedLessons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveForm = async () => {
    if (!editTarget || saving) return;
    setFormError(null);

    const slug = form.slug.trim();
    const titleEn = form.titleEn.trim();
    const titleRu = form.titleRu.trim();
    const sortOrder = Number(form.sortOrder);

    if (!slug) return setFormError(t('admin.theoryTopicsSlugRequired'));
    if (!titleEn && !titleRu) return setFormError(t('admin.theoryTopicsTitleRequired'));
    if (!Number.isFinite(sortOrder)) return setFormError(t('admin.theoryTopicsSortInvalid'));

    const payload = {
      slug,
      title: titleEn || titleRu,
      title_en: titleEn,
      title_ru: titleRu,
      sort_order: sortOrder,
    };

    setSaving(true);
    try {
      let url = '';
      let method = 'POST';
      let body: Record<string, unknown> = { ...payload };

      if (editTarget.kind === 'lesson' && editTarget.mode === 'create') {
        url = `${API_BASE}/api/theory/admin/lessons`;
        body.category_id = editTarget.categoryId;
      } else if (editTarget.kind === 'lesson' && editTarget.mode === 'edit') {
        url = `${API_BASE}/api/theory/admin/lessons/${editTarget.lesson.id}`;
        method = 'PATCH';
      } else if (editTarget.kind === 'sublesson' && editTarget.mode === 'create') {
        url = `${API_BASE}/api/theory/admin/sublessons`;
        body = { ...payload, lesson_id: editTarget.lessonId };
      } else if (editTarget.kind === 'sublesson' && editTarget.mode === 'edit') {
        url = `${API_BASE}/api/theory/admin/sublessons/${editTarget.sublesson.id}`;
        method = 'PATCH';
      }

      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }

      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setFormError(data?.message || t('admin.theoryTopicsSaveFailed'));
        return;
      }

      setEditTarget(null);
      await loadLibrary();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('admin.theoryTopicsSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const deleteLesson = async (lessonId: number) => {
    if (deletingLessonId != null) return;
    if (!window.confirm(t('admin.theoryTopicsDeleteLessonConfirm'))) return;

    setDeletingLessonId(lessonId);
    try {
      const r = await fetch(`${API_BASE}/api/theory/admin/lessons/${lessonId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(t('admin.theoryTopicsDeleteFailed'));
      await loadLibrary();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.theoryTopicsDeleteFailed'));
    } finally {
      setDeletingLessonId(null);
    }
  };

  const deleteSublesson = async (sublessonId: number) => {
    if (deletingSublessonId != null) return;
    if (!window.confirm(t('admin.theoryTopicsDeleteSublessonConfirm'))) return;

    setDeletingSublessonId(sublessonId);
    try {
      const r = await fetch(`${API_BASE}/api/theory/admin/sublessons/${sublessonId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(t('admin.theoryTopicsDeleteFailed'));
      await loadLibrary();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.theoryTopicsDeleteFailed'));
    } finally {
      setDeletingSublessonId(null);
    }
  };

  const modalTitle =
    editTarget?.kind === 'lesson'
      ? editTarget.mode === 'create'
        ? t('admin.theoryTopicsAddLesson')
        : t('admin.theoryTopicsEditLesson')
      : editTarget?.mode === 'create'
        ? t('admin.theoryTopicsAddSublesson')
        : t('admin.theoryTopicsEditSublesson');

  return (
    <div className="admin-theory-topics">
      <div className="admin-reports-header">
        <h1 className="admin-reports-title">{t('admin.theoryTopicsTitle')}</h1>
      </div>

      {error && (
        <div className="admin-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-loading-inline">{t('admin.theoryTopicsLoading')}</div>
      ) : categories.length === 0 ? (
        <p className="admin-table-empty">{t('admin.theoryTopicsEmpty')}</p>
      ) : (
        <div className="admin-theory-topics-tree">
          {categories.map((category) => {
            const isCategoryOpen = expandedCategories.has(category.id);
            return (
              <section key={category.id} className="admin-theory-topics-category">
                <div className="admin-theory-topics-category-head">
                  <button
                    type="button"
                    className="admin-theory-topics-toggle"
                    onClick={() => toggleCategory(category.id)}
                    aria-expanded={isCategoryOpen}
                  >
                    {isCategoryOpen ? '▾' : '▸'} {displayTitle(category, lang)}
                    <span className="admin-theory-topics-meta">({category.slug})</span>
                  </button>
                  <button
                    type="button"
                    className="admin-secondary-btn"
                    onClick={() => openCreateLesson(category.id)}
                  >
                    {t('admin.theoryTopicsAddLesson')}
                  </button>
                </div>

                {isCategoryOpen && (
                  <ul className="admin-theory-topics-lessons">
                    {category.lessons.length === 0 ? (
                      <li className="admin-theory-topics-empty-item">{t('admin.theoryTopicsNoLessons')}</li>
                    ) : (
                      category.lessons.map((lesson) => {
                        const isLessonOpen = expandedLessons.has(lesson.id);
                        return (
                          <li key={lesson.id} className="admin-theory-topics-lesson">
                            <div className="admin-theory-topics-lesson-head">
                              <button
                                type="button"
                                className="admin-theory-topics-toggle"
                                onClick={() => toggleLesson(lesson.id)}
                                aria-expanded={isLessonOpen}
                              >
                                {isLessonOpen ? '▾' : '▸'}{' '}
                                <strong>{displayTitle(lesson, lang)}</strong>
                                <span className="admin-theory-topics-meta">
                                  #{lesson.sort_order} · {lesson.slug}
                                </span>
                              </button>
                              <div className="admin-theory-topics-actions">
                                <button
                                  type="button"
                                  className="admin-secondary-btn"
                                  onClick={() => openCreateSublesson(lesson.id)}
                                >
                                  {t('admin.theoryTopicsAddSublesson')}
                                </button>
                                <button
                                  type="button"
                                  className="admin-secondary-btn"
                                  onClick={() => openEditLesson(lesson, category.id)}
                                >
                                  {t('admin.problemsEdit')}
                                </button>
                                <button
                                  type="button"
                                  className="admin-danger-btn"
                                  onClick={() => deleteLesson(lesson.id)}
                                  disabled={deletingLessonId === lesson.id}
                                >
                                  {deletingLessonId === lesson.id
                                    ? t('admin.theoryTopicsDeleting')
                                    : t('admin.problemsDelete')}
                                </button>
                              </div>
                            </div>

                            {isLessonOpen && (
                              <ul className="admin-theory-topics-sublessons">
                                {lesson.sublessons.length === 0 ? (
                                  <li className="admin-theory-topics-empty-item">
                                    {t('admin.theoryTopicsNoSublessons')}
                                  </li>
                                ) : (
                                  lesson.sublessons.map((sublesson) => (
                                    <li key={sublesson.id} className="admin-theory-topics-sublesson">
                                      <span className="admin-theory-topics-sublesson-title">
                                        {displayTitle(sublesson, lang)}
                                        <span className="admin-theory-topics-meta">
                                          #{sublesson.sort_order} · {sublesson.slug}
                                        </span>
                                      </span>
                                      <div className="admin-theory-topics-actions">
                                        <button
                                          type="button"
                                          className="admin-secondary-btn"
                                          onClick={() => openEditSublesson(sublesson, lesson.id)}
                                        >
                                          {t('admin.problemsEdit')}
                                        </button>
                                        <button
                                          type="button"
                                          className="admin-danger-btn"
                                          onClick={() => deleteSublesson(sublesson.id)}
                                          disabled={deletingSublessonId === sublesson.id}
                                        >
                                          {deletingSublessonId === sublesson.id
                                            ? t('admin.theoryTopicsDeleting')
                                            : t('admin.problemsDelete')}
                                        </button>
                                      </div>
                                    </li>
                                  ))
                                )}
                              </ul>
                            )}
                          </li>
                        );
                      })
                    )}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {editTarget && (
        <div className="admin-modal-overlay" role="presentation" onClick={() => setEditTarget(null)}>
          <div
            className="admin-modal admin-theory-topics-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="theory-topics-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="theory-topics-modal-title" className="admin-modal-title">
              {modalTitle}
            </h2>

            {formError && (
              <div className="admin-error" role="alert">
                {formError}
              </div>
            )}

            <div className="admin-theory-topics-form">
              <label className="admin-field">
                <span className="admin-field-label">{t('admin.theoryTopicsSlug')}</span>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                  placeholder="select-columns"
                />
              </label>
              <label className="admin-field">
                <span className="admin-field-label">{t('admin.theoryTopicsTitleEn')}</span>
                <input
                  type="text"
                  value={form.titleEn}
                  onChange={(e) => setForm((prev) => ({ ...prev, titleEn: e.target.value }))}
                />
              </label>
              <label className="admin-field">
                <span className="admin-field-label">{t('admin.theoryTopicsTitleRu')}</span>
                <input
                  type="text"
                  value={form.titleRu}
                  onChange={(e) => setForm((prev) => ({ ...prev, titleRu: e.target.value }))}
                />
              </label>
              <label className="admin-field admin-field-narrow">
                <span className="admin-field-label">{t('admin.theoryTopicsSort')}</span>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
                  min={0}
                />
              </label>
            </div>

            <div className="admin-modal-actions">
              <button
                type="button"
                className="admin-secondary-btn"
                onClick={() => setEditTarget(null)}
                disabled={saving}
              >
                {t('admin.problemsFormCancel')}
              </button>
              <button
                type="button"
                className="admin-primary-btn"
                onClick={saveForm}
                disabled={saving}
              >
                {saving ? t('admin.theoryTopicsSaving') : t('admin.problemsFormSave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
